// AvatarStack — overlapping row of avatars with `+N` overflow chip.
// Mirrors AvatarStack() in /Users/harry/Downloads/employee-portal/project/components.jsx
// (lines 37–54). Each subsequent avatar gets margin-left -8px and a descending z-index.
// The `+N` chip uses azurite-500 background, white text, same avatar size.

import { Box } from "@chakra-ui/react";
import { forwardRef } from "react";
import Avatar, { AvatarSize } from "./Avatar";

export interface AvatarStackPerson {
  name: string;
  src?: string;
  tone?: string;
}

export interface AvatarStackProps {
  people: AvatarStackPerson[];
  /** Avatar size. Default `sm` (24×24). */
  size?: AvatarSize;
  /** Max avatars shown before collapsing into a `+N` chip. Default 3. */
  max?: number;
}

// Per-size dimensions + font sizes for the `+N` chip — must match Avatar.
const sizeMap: Record<AvatarSize, { px: number; fontSize: string }> = {
  xs: { px: 20, fontSize: "10px" },
  sm: { px: 24, fontSize: "11px" },
  md: { px: 32, fontSize: "11px" },
  lg: { px: 48, fontSize: "16px" },
  xl: { px: 56, fontSize: "18px" },
};

const AvatarStack = forwardRef<HTMLSpanElement, AvatarStackProps>(
  ({ people, size = "sm", max = 3 }, ref) => {
    const shown = people.slice(0, max);
    const extra = people.length - shown.length;
    const { px, fontSize } = sizeMap[size];

    return (
      <Box as="span" ref={ref} display="inline-flex" alignItems="center">
        {shown.map((p, i) => (
          <Box
            as="span"
            key={`${p.name}-${i}`}
            display="inline-flex"
            marginLeft={i === 0 ? 0 : "-8px"}
            zIndex={shown.length - i}
          >
            <Avatar name={p.name} src={p.src} tone={p.tone} size={size} />
          </Box>
        ))}
        {extra > 0 && (
          <Box
            as="span"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            overflow="hidden"
            borderRadius="full"
            color="white"
            fontFamily="inherit"
            fontWeight={600}
            lineHeight="1"
            width={`${px}px`}
            height={`${px}px`}
            fontSize={fontSize}
            bg="p.500"
            marginLeft="-8px"
            zIndex={0}
          >
            +{extra}
          </Box>
        )}
      </Box>
    );
  },
);

AvatarStack.displayName = "AvatarStack";

export default AvatarStack;
